// Five new service layouts, from the everyday kit: the big announcement, the
// treatment editorial, what's included, the invitation and the spotlight.
import { flow, textBlock, brand, footer, rule, photo, tag, panel, display, label, body, strong, widthOf } from '../../kit'
import { base, ground, serif, mono, heavy, detailLine, lines } from './common'

export default {
  // NEW, as big as it goes, then the service
  launch(p, env) {
    const { fr, c, w } = base(env)
    const on = ground(p, env, true)
    flow(p, fr, [
      brand(env, fr, on),
      textBlock(c.eyebrow, () => ({ ...label(env, on), x: fr.left, w }), { gap: 60, slot: 'eyebrow' }),
      textBlock('New', s => ({ ...heavy(on, Math.round(250 * s)), upper: true, x: fr.left - 12, w: w + 12, maxLines: 1 }), { gap: 34 }),
      rule(fr, on, { gap: 40 }),
      textBlock(c.headline, s => ({ ...display(env, on, 120 * s), x: fr.left - 5, w: w + 5, maxLines: 3 }), { gap: 56, slot: 'headline' }),
      textBlock(c.body, s => ({ ...body(env, on, Math.round(46 * s)), x: fr.left, w: Math.min(w, 840), maxLines: 3 }), { gap: 44, slot: 'body' }),
      detailLine(env, fr, c.detail, on),
      footer(env, fr, on),
    ])
  },

  // A campaign photo with an editorial caption under it
  treatment(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    flow(p, fr, [
      brand(env, fr, ink),
      {
        gap: 46,
        flex: 1,
        draw: (pp, y, s) => {
          const h = Math.round(660 * s)
          photo(pp, env, 'main', { x: fr.left, y, w, h })
          if (!pp.isDry) tag(pp, env, 'New at the studio', fr.left, y + h - 66, k.accent, k.on)
          return y + h
        },
      },
      textBlock(c.headline, s => ({ ...display(env, ink, 104 * s), x: fr.left - 4, w: w + 4, maxLines: 2 }), { gap: 40, slot: 'headline' }),
      textBlock(c.eyebrow, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 22, slot: 'eyebrow' }),
      textBlock(c.body, () => ({ ...body(env, ink, 34), x: fr.left, w, maxLines: 2 }), { gap: 12, slot: 'body' }),
      footer(env, fr, ink),
    ])
  },

  // The three things they get, numbered in discs
  included(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const items = lines(c.items)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.eyebrow, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, ink, 118 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 28, slot: 'headline' }),
      items.length ? {
        gap: 64,
        flex: 1,
        draw: (pp, y, s) => {
          const r = Math.round(42 * s)
          const tx = fr.left + r * 2 + 36
          let yy = y
          items.forEach((t, i) => {
            pp.circle(fr.left + r, yy + r, r, k.accent)
            const ns = Math.max(30, Math.round(36 * s))
            pp.text(String(i + 1), { ...strong(env, k.on, ns), x: fr.left, y: yy + r - Math.round(ns * 0.6), w: r * 2, align: 'center', maxLines: 1 })
            const tt = pp.text(t, { ...strong(env, ink, Math.round(46 * s)), x: tx, y: yy + Math.max(0, r - 30), w: fr.right - tx, maxLines: 2, slot: 'items' })
            const end = Math.max(tt.bottom, yy + r * 2) + Math.round(26 * s)
            pp.line(tx, end, fr.right, end, ink, 2)
            yy = end + Math.round(i < items.length - 1 ? 34 * s : 0)
          })
          return yy
        },
      } : null,
      detailLine(env, fr, c.detail, ink),
      footer(env, fr, ink),
    ])
  },

  // A ticket to try it: ADMIT ONE, a perforation and a line about it
  invite(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const pad = 36
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.eyebrow, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, ink, 128 * s), x: fr.left - 5, w: w + 5, maxLines: 3 }), { gap: 28, slot: 'headline' }),
      {
        gap: 70,
        flex: 1,
        draw: (pp, y, s) => panel(pp, {
          x: fr.left, y, w, bg: k.accent, pad,
          content: (q, top) => {
            const a = q.text('Admit one', { ...mono(k.on, 32), weight: 700, upper: true, x: fr.left + pad, y: top, w: w - pad * 2, maxLines: 1 })
            const dy = a.bottom + 34
            q.line(fr.left + pad, dy, fr.right - pad, dy, k.on, 2, [10, 10])
            q.circle(fr.left, dy, 20, k.paper)
            q.circle(fr.right, dy, 20, k.paper)
            return q.text(c.body || '', { ...serif(env, k.on, Math.round(56 * s)), x: fr.left + pad, y: dy + 46, w: w - pad * 2 - 40, maxLines: 3, slot: 'body' }).bottom + 20
          },
        }),
      },
      { gap: 0, flex: 1, draw: (pp, y) => y },
      detailLine(env, fr, c.detail, ink),
      footer(env, fr, ink),
    ])
  },

  // The service beside a photo, with the price and time in the colour block
  spotlight(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const gap = 24
    const bw = Math.round((w - gap) * 0.45)
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.eyebrow, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, ink, 124 * s), x: fr.left - 5, w: w + 5, maxLines: 2 }), { gap: 28, slot: 'headline' }),
      {
        gap: 64,
        flex: 1,
        draw: (pp, y, s) => {
          const h = Math.round(520 * s)
          const pad = 28
          pp.rect(fr.left, y, bw, h, k.accent)
          photo(pp, env, 'main', { x: fr.left + bw + gap, y, w: w - bw - gap, h })
          const d = c.detail ? pp.text(c.detail, { ...strong(env, k.on, 38), x: fr.left + pad, y: y + h - pad - 100, w: bw - pad * 2, maxLines: 2, maxH: 100, valign: 'bottom', slot: 'detail' }) : { top: y + h - pad }
          pp.text(c.body || '', { ...strong(env, k.on, Math.round(48 * s)), lh: 1.12, x: fr.left + pad, y: y + pad, w: bw - pad * 2, maxLines: 6, maxH: d.top - y - pad * 2, slot: 'body' })
          return y + h
        },
      },
      textBlock('New service / Your next visit', () => ({ ...label(env, ink), x: fr.left, w: widthOf(fr) }), { gap: 40 }),
      footer(env, fr, ink),
    ])
  },
}
